"use client";

import { useSyncExternalStore } from "react";

export function storedChoice<T extends string | number>(key: string, parse: (raw: string | null) => T, fallback: T): () => readonly [T, (next: T) => void] {
  const listeners = new Set<() => void>();
  let chosen: T | null = null;

  function read(): T {
    if (chosen !== null) return chosen;
    try {
      return parse(window.localStorage.getItem(key));
    } catch {
      return fallback;
    }
  }

  function subscribe(onChange: () => void): () => void {
    const onStorage = () => { chosen = null; onChange(); };
    listeners.add(onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      listeners.delete(onChange);
      window.removeEventListener("storage", onStorage);
    };
  }

  function set(next: T): void {
    chosen = next;
    try {
      window.localStorage.setItem(key, String(next));
    } catch {}
    for (const listener of listeners) listener();
  }

  return function useStoredChoice() {
    return [useSyncExternalStore(subscribe, read, () => fallback), set] as const;
  };
}
